// TicketDetailView.jsx — the shared read-only ticket detail body (batch 50).
// Extracted from MyTicketsPage's file-local panel so the approver surface
// (/approvals, the SB-064 interim slice) renders the same body/feedback/
// attachments/timeline block without duplicating it. Endpoint wiring comes in
// via props (the me family and the approver family differ in fetcher, download
// URL and who counts as "me" in the feedback chips); action surfaces (the T7/T8
// reply box, future R1 ruling actions) come in as `footer`.
import { useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { errKey } from '../api/errText.mjs';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import GroupChip from './GroupChip.jsx';
import MarkdownBody from './MarkdownBody.jsx';
import ResponseDeadlineBar from './ResponseDeadlineBar.jsx';
import { fmtTime } from './fmtTime.mjs';
import { isPending, isUnverified, localeToShow } from '../pages/locales.mjs';

// fmtTime 住在 fmtTime.mjs（第二百四十七批复审：钟点条也要它，而本文件引钟点条——留在这里
// 就是两个 .jsx 互引成环）。这里再导出一次，十几处 `import { fmtTime } from '…/TicketDetailView.jsx'`
// 的既有调用方原地不动；新代码请直接引 fmtTime.mjs。
export { fmtTime };

function statusLabel(t, s) {
  if (!s) return '';
  const k = `ticketStatus.${s}`;
  const v = t(k);
  return v === k ? s : v;
}

// One timeline entry as a single text line. T0 has no fromStatus; archive /
// unarchive ops move no status at all, so the op label is the only text left.
// Op codes and KNOWN system-generated note tokens map through i18n; free-text
// operator notes stay verbatim (batch-43 language rule).
function opLabel(t, op) {
  const k = `ticketOp.${op}`;
  const v = t(k);
  return v === k ? op : v;
}

const SYSTEM_NOTE_KEYS = {
  auto: 'ticketNote.auto',
  'timeout requeue (§5.2)': 'ticketNote.timeoutRequeue',
};

export function timelineText(t, tr) {
  const from = statusLabel(t, tr.fromStatus);
  const to = statusLabel(t, tr.toStatus);
  let s = from && to ? `${from} → ${to}` : to || from || opLabel(t, tr.op);
  if (tr.note) {
    const nk = SYSTEM_NOTE_KEYS[tr.note];
    s += ` · ${nk ? t(nk) : tr.note}`;
  }
  return s;
}

// ── 附件警示的判据（第五十七批 D8）─────────────────────────────────────────────
// 标记之间是**不依赖任何 import 的纯 JS**：node --test 解析不了 JSX，而这条判据就是
// 本次修复的全部内容，只靠"把源码当文本读"来测太弱。单测 ticketDetailCaution.test.mjs
// 按下面这对标记把它切出来直接求值，跑的是真正上线的这几行。
// <caution-fn>
function attachmentCaution(attach) {
  // WHY 每一个 .csv 都要带警示，而不只是后端标了 formulaRisk 的那些：
  // 后端那个探测器是**尽力而为的升级信号，不是闸**。对抗性复核里，`""=cmd…` 这样
  // 两个字符的前缀就让它返回 false；一处未闭合的引号还会让它对文件其余部分全盲。
  // 而按 SOP-09 逐个下载、逐个打开附件的正是工位上的评审人——受害者是人。只在探测
  // 命中时才出提示，等于把"这个文件安全"的结论押在一个可被绕过的解析器上，绕过一次
  // 评审人就什么都看不到。所以真正的控制是"每一个表格文件都提醒一句"，探测命中只把
  // 语气从提醒升级成告警（strong）。附件是证据、字节一个都不许改，这条提示是这条
  // 链路上唯一的防线。
  if (attach && attach.formulaRisk) return { key: 'attach.formulaRisk', strong: true };
  const name = String((attach && attach.filename) || '');
  // 安装包（第二百四十一批）：可执行体，纪律与表格文件相反——不是「小心打开」而是「不要打开」。
  // 判据按扩展名而不按后端的 kind 字段：kind 缺席（旧面、旧行）时提示照出，与 csv 同一姿态。
  if (/\.apk$/i.test(name)) return { key: 'attach.packageCaution', strong: true };
  // 压缩包（第二百七十五批）：它自己不是可执行体，但它**可能装着**可执行体，所以语气不到 strong，
  // 提示的内容也不是「不要打开」而是「按什么次序打开」——先读正文声明、只在 temp/ 下解包。
  // 同样按扩展名判：kind 缺席的旧行也要出提示。
  if (/\.zip$/i.test(name)) return { key: 'attach.archiveCaution', strong: false };
  return /\.csv$/i.test(name) ? { key: 'attach.csvCaution', strong: false } : null;
}
// </caution-fn>

export default function TicketDetailView({
  no,
  load,
  attachmentUrl,
  authorLabels, // {submitter: i18nKey, handler: i18nKey}; submitter chip is primary-colored
  header = null, // optional node above the body (e.g. the approver's submitter line)
  footer = null, // optional node below (e.g. the member's info_requested reply box)
  onData = null, // called with the loaded detail whenever a fetch succeeds
}) {
  const t = useT();
  const { lang } = useLang();
  const detail = useLoad(load, [no, lang]);

  // onData 走 ref：调用侧多半是行内箭头函数，直接进依赖表会每渲染一轮就再触发一次，
  // 而它通常又会 setState —— 自激成死循环。这里只在**取数结果变化**时通知一次。
  const onDataRef = useRef(onData);
  useEffect(() => {
    onDataRef.current = onData;
  });
  useEffect(() => {
    if (detail.status === 'ready' && onDataRef.current) onDataRef.current(detail.data || {});
  }, [detail.status, detail.data]);

  // WHY footer 在三个分支里都要渲染（第五十七批 D2）：footer 里装的是「补传附件」挑选器，
  // 里面可能正留着一次部分失败之后剩下的文件。原来只有 ready 分支渲染 footer，于是
  // 一次慢的或失败的重取（上传成功后本组件恰恰会被重挂一次去刷新附件清单）就让挑选器
  // 整个从屏幕上消失——正好是那些文件最需要被看见、被重试的时刻。
  if (detail.status === 'error') {
    return (
      <Stack spacing={2} sx={{ py: 2, px: { xs: 0.5, sm: 1 } }}>
        <Alert severity="error">{t(errKey(detail.error, { fallback: 'tickets.error' }))}</Alert>
        {footer}
      </Stack>
    );
  }
  if (detail.status !== 'ready') {
    return (
      <Stack spacing={2} sx={{ py: 2, px: { xs: 0.5, sm: 1 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} />
        </Box>
        {footer}
      </Stack>
    );
  }
  const d = detail.data || {};
  const feedback = Array.isArray(d.feedback) ? d.feedback : [];
  const timeline = Array.isArray(d.timeline) ? d.timeline : [];
  const attachments = Array.isArray(d.attachments) ? d.attachments : [];

  return (
    <Stack spacing={2} sx={{ py: 2, px: { xs: 0.5, sm: 1 } }}>
      {/* 第一百零三批：详情头部的组徽标。两个路由族（me / approver）的详情投影
          都带 projectGroupId（ticket_row_json），所以放在共享组件里一处成立——
          审批人展开工单裁决时，第一眼就是这张单属于哪个组（GroupChip 头注）。 */}
      {/* SB-190 §8（第二百四十七批，步 5）：指派单的钟点条与「派生自」芯片同在这一行。
          responseDeadline 只在 ASK- 的详情上非 null（me 家族的详情投影，与 sla mine／
          管理页同一只 clock_for_item）；放在共享组件里，两位当事人看到的是同一条——
          发出方看「他还欠我」，受指派人看「我还欠他」。非 ASK 一律 null，条不出现。 */}
      {d.projectGroupId != null || d.responseDeadline || d.derivedFromNo ? (
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {d.projectGroupId != null ? <GroupChip groupId={d.projectGroupId} /> : null}
          {d.derivedFromNo ? (
            <Chip size="small" variant="outlined"
                  sx={{ fontFamily: 'ui-monospace, monospace' }}
                  label={t('tickets.detail.derivedFrom', { no: d.derivedFromNo })} />
          ) : null}
          <ResponseDeadlineBar deadline={d.responseDeadline} />
        </Stack>
      ) : null}
      {typeof header === 'function' ? header(d) : header}

      <Box>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('tickets.detail.body')}
        </Typography>
        <MarkdownBody body={d.body} format={d.bodyFormat} />
      </Box>

      {/* SB-067 二期：机器译文**徐呈**。它是另一块，不是把上面那块换成别的语言——
          写成 toggle 替换的话，看的人不知道自己正在读的是哪一份。
          OQ-B27：原文始终并存且为准。 */}
      {(() => {
        const loc = localeToShow(d.locales, lang, d.submitLang);
        if (loc) {
          return (
            <Box sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
              {/* flexWrap：en 的「machine translation · unverified」Chip 在 360px 下会被
                  省略号截断——这枚 Chip 的全部意义就是那行字，掉行保全文 */}
              <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('locale.heading', { lang: loc.lang })}
                </Typography>
                {isUnverified(loc) ? (
                  <Chip size="small" variant="outlined" label={t('locale.machine')} />
                ) : null}
              </Stack>
              {loc.title ? (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{loc.title}</Typography>
              ) : null}
              {/* 第八十八批：译文跟随**原文**的 bodyFormat——同一篇正文的另一种语言，
                  格式不可能不同；给它自己的字段就是给同一件事两个答案。 */}
              <MarkdownBody body={loc.body} format={d.bodyFormat} />
            </Box>
          );
        }
        // 什么都不显示会让人以为这个系统没有翻译功能，而它只是还没跑完。
        return isPending(d.locales, lang, d.submitLang) ? (
          <Typography variant="caption" color="text.secondary">
            {t('locale.pending')}
          </Typography>
        ) : null;
      })()}

      {feedback.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('tickets.detail.feedback')}
          </Typography>
          <Stack spacing={1}>
            {feedback.map((f, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                  {/* SB-190 §8：第三种作者——受指派人（A1 的反馈，store 打标
                      author_kind='assignee'）。ApprovalsPage 传自己的 authorLabels、
                      没有 assignee 一项，`??` 回落到成员面的键，那一页不必改。 */}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={f.authorKind === 'submitter' ? 'primary'
                      : f.authorKind === 'assignee' ? 'warning' : 'default'}
                    label={t(
                      f.authorKind === 'submitter' ? authorLabels.submitter
                        : f.authorKind === 'assignee'
                          ? (authorLabels.assignee ?? 'tickets.detail.author.assignee')
                          : authorLabels.handler,
                    )}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {fmtTime(f.atMs, lang)}
                  </Typography>
                </Stack>
                {/* 反馈跟随本工单的 bodyFormat：回复框里写的和正文里写的是同一个人、
                    同一种习惯，给反馈单开一列会逼着回复框也长出一个格式选择器。 */}
                <MarkdownBody body={f.body} format={d.bodyFormat} />
              </Paper>
            ))}
          </Stack>
        </Box>
      ) : null}

      {attachments.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('tickets.detail.attachments')}
          </Typography>
          <Stack spacing={0.5}>
            {attachments.map((a) => {
              // 表格类附件的公式注入提示（第五十六批起）。以 =/+/-/@ 开头的单元格在
              // Excel/LibreOffice 打开时会被当作公式执行，而按 SOP-09 逐个下载打开
              // 附件的正是工作空间的审核人——风险落在人身上，不在服务器上。附件是
              // 证据，字节不得改动，所以防护只能是"告知"。第五十七批 D8 起，门条件
              // 从"后端标了 formulaRisk"放宽到"凡 .csv 都提醒"——理由见
              // attachmentCaution()：探测器可被绕过，绕过一次审核人就一个字都看不到。
              const caution = attachmentCaution(a);
              return (
                <Box key={a.id}>
                  <MuiLink
                    href={attachmentUrl(no, a.id)}
                    target="_blank"
                    rel="noopener"
                    variant="body2"
                  >
                    {a.filename}
                  </MuiLink>
                  {caution ? (
                    <Typography sx={{ display: 'block' }}
                      variant="caption"
                      color={caution.strong ? 'warning.main' : 'text.secondary'}
                    >
                      ⚠ {t(caution.key)}
                    </Typography>
                  ) : null}
                  {a.sha256 ? (
                    <Typography sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}
                      variant="caption"
                      color="text.secondary"
                    >
                      sha256 {a.sha256}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      {timeline.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('tickets.detail.timeline')}
          </Typography>
          <Stack spacing={0.25}>
            {timeline.map((tr, i) => (
              <Typography key={i} variant="caption" color="text.secondary">
                {fmtTime(tr.atMs, lang)} · {timelineText(t, tr)}
              </Typography>
            ))}
          </Stack>
        </Box>
      ) : null}

      {footer}
    </Stack>
  );
}
